import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';
import { useReconStore } from '@/stores/useReconStore';
import { toast } from 'sonner';
import { Upload, Check, AlertCircle } from 'lucide-react';
import * as Papa from 'papaparse';

export default function ReconImport() {
  const { activeOrg } = useOrgStore();
  const { setCurrentImport, updateImportStatus } = useReconStore();
  const [importing, setImporting] = useState(false);
  const [importName, setImportName] = useState('');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<any>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!importName) {
      toast.error('Please enter an import name');
      return;
    }

    try {
      setImporting(true);
      setProgress(10);

      // Parse CSV
      Papa.parse(file, {
        header: true,
        complete: async (results) => {
          setProgress(30);

          // Map CSV columns to our format
          const records = results.data
            .filter((row: any) => row.Date && row.Amount)
            .map((row: any) => ({
              txn_date: row.Date || row.date || row['Transaction Date'],
              ref: row.Reference || row.ref || row.Description || '',
              amount: parseFloat(row.Amount || row.amount || '0'),
              description: row.Description || row.description || '',
            }));

          if (records.length === 0) {
            throw new Error('No valid records found in CSV');
          }

          setProgress(50);

          // Call edge function
          const { data, error } = await supabase.functions.invoke('lms-recon-import', {
            body: {
              org_id: activeOrg,
              import_name: importName,
              records,
            },
          });

          if (error) throw error;

          setProgress(100);
          setResult(data);

          toast.success(
            `Import complete: ${data.matched_count} matched, ${data.unmatched_count} unmatched`
          );

          setCurrentImport({
            id: data.import_id,
            name: importName,
            status: 'complete',
            record_count: records.length,
            matched_count: data.matched_count,
            unmatched_count: data.unmatched_count,
          });

          setImportName('');
        },
        error: (error) => {
          throw new Error(`CSV parse error: ${error.message}`);
        },
      });
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed: ' + error.message);
    } finally {
      setImporting(false);
      setProgress(0);
    }
  }, [activeOrg, importName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    disabled: importing,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Bank Reconciliation Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="import-name">Import Name</Label>
            <Input
              id="import-name"
              placeholder="e.g. January 2024 - FNB"
              value={importName}
              onChange={(e) => setImportName(e.target.value)}
              disabled={importing}
            />
          </div>

          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-border'}
              ${importing ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary'}
            `}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-lg font-medium">Drop the CSV file here...</p>
            ) : (
              <>
                <p className="text-lg font-medium mb-2">
                  Drag and drop bank statement CSV
                </p>
                <p className="text-sm text-muted-foreground">
                  or click to select file
                </p>
              </>
            )}
          </div>

          {importing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Processing...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {result && (
            <Card className="border-green-500/20 bg-green-500/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium mb-2">Import Successful</p>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Matched transactions:</span>
                        <span className="font-medium text-green-500">
                          {result.matched_count}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Unmatched transactions:</span>
                        <span className="font-medium text-yellow-500">
                          {result.unmatched_count}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
